// src/pages/SupplierDashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import {
  Layers, BookOpenCheck, GraduationCap, Wallet, Users, Ticket, CheckCircle2,
  CalendarClock, TrendingUp, School,
} from 'lucide-react';
import { useSupplierDashboard } from '../contexts/SupplierDashboardContext';
import { useSupplierPrograms } from '../contexts/SupplierProgramsContext';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';
import SupplierDashboardHeader from '../components/ui/SupplierDashboardHeader';
import { KpiCard, Panel, StatRow, COLORS, ChartTooltipStyle } from '../components/ui/SupplierDashboardUI';

/* ── Helpers ─────────────────────────────────────────────────────── */

const fmtCurrency = (n) =>
  `₪${Number(n || 0).toLocaleString('he-IL', { maximumFractionDigits: 0 })}`;

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
  } catch {
    return d;
  }
};

const fmtMonth = (ym) => {
  if (!ym) return ym;
  const [y, m] = String(ym).split('-');
  const names = ['ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יונ', 'יול', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ'];
  const idx = Number(m) - 1;
  return names[idx] ? `${names[idx]} ${y.slice(2)}` : ym;
};

const STATUS_LABELS = {
  DRAFT: 'טיוטה',
  PENDING: 'ממתין לאישור',
  APPROVED: 'מאושר',
  ACTIVE: 'פעיל',
  SUSPENDED: 'מושעה',
  CLOSED: 'סגור',
};

const PIE_COLORS = [COLORS.indigo, COLORS.emerald, COLORS.amber, COLORS.rose, COLORS.slate, COLORS.purple];

const currencyTooltip = (value) => [fmtCurrency(value), 'הכנסה'];

/* ── Page ────────────────────────────────────────────────────────── */

const SupplierDashboard = () => {
  const { programs, loading: programsLoading, fetchPrograms } = useSupplierPrograms();
  const {
    overview, publicCoursesSummary, upcomingLessons, loading, fetchDashboard,
    months, programNames, registrationGrowth, couponsTrend, couponsByProgram,
    publicRegistrationGrowth, trendsLoading, fetchTrends,
  } = useSupplierDashboard();

  // 'all' | 'public' | <programId as string>
  const [scope, setScope] = useState('all');

  // Separate from the context's own `loading`/`overview` — those
  // persist across navigation (the provider never unmounts, only this
  // page does), so `overview` stays truthy from a PREVIOUS visit even
  // while a fresh fetch is running on THIS visit. Gating the spinner
  // on `!overview` therefore only ever shows it the very first time
  // ever, not on every navigation back to this page. `pageReady` is
  // scoped to this mount specifically, so it's always false at first
  // render regardless of what's already sitting in the context.
  const [pageReady, setPageReady] = useState(false);

  // Same reasoning as SupplierPrograms.jsx: the providers wrap the
  // whole app and only auto-fetch once on login, not on every visit
  // to this specific page — so this page needs its own fetch-on-mount
  // to guarantee fresh data (and a real loading window) every time
  // someone actually navigates here.
  useEffect(() => {
    setPageReady(false);
    Promise.all([fetchDashboard(), fetchTrends(), fetchPrograms()]).finally(() => {
      setPageReady(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const relevantPrograms = useMemo(
    () => (Array.isArray(programs) ? programs : []).filter((p) => p.status !== 'DRAFT'),
    [programs]
  );

  const isAllScope = scope === 'all';
  const isPublicScope = scope === 'public';
  const selectedProgram = useMemo(
    () => (isAllScope || isPublicScope ? null : relevantPrograms.find((p) => String(p.id) === scope) || null),
    [isAllScope, isPublicScope, relevantPrograms, scope]
  );
  // A program the picker was pointed at that's since disappeared
  // (e.g. filtered out, deleted) — fall back to 'all' rather than
  // silently showing nothing.
  useEffect(() => {
    if (!isAllScope && !isPublicScope && !selectedProgram && relevantPrograms.length > 0) {
      setScope('all');
    }
  }, [isAllScope, isPublicScope, selectedProgram, relevantPrograms]);

  // Chart data — derived from the same figures already shown in the
  // KPI cards/table above, just visualized. No extra API calls.
  const revenueByProgramData = useMemo(
    () =>
      relevantPrograms
        .map((p) => ({ name: p.name, revenue: Number(p.revenue || 0) }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 8), // keep the chart readable if there are many programs
    [relevantPrograms]
  );

  const programsByStatusData = useMemo(() => {
    const byStatus = overview?.programsByStatus || {};
    return Object.entries(byStatus)
      .filter(([, count]) => Number(count) > 0)
      .map(([status, count]) => ({ name: STATUS_LABELS[status] || status, value: Number(count) }));
  }, [overview]);

  const courseSplitData = useMemo(() => {
    const program = Number(overview?.programCourses || 0);
    const publicC = Number(overview?.publicCourses || 0);
    return [
      { name: 'קורסי תוכנית', value: program },
      { name: 'קורסים ציבוריים', value: publicC },
    ].filter((d) => d.value > 0);
  }, [overview]);

  // Registration growth: same rows the backend returns (already
  // cumulative per program), just with display-formatted month labels.
  const registrationGrowthData = useMemo(
    () => registrationGrowth.map((row) => ({ ...row, monthLabel: fmtMonth(row.month) })),
    [registrationGrowth]
  );

  // Which line(s) the growth chart actually draws — every program in
  // the "all" scope, just the one selected program otherwise.
  const activeProgramNames = isAllScope ? programNames : selectedProgram ? [selectedProgram.name] : [];

  const couponsTrendData = useMemo(
    () => couponsTrend.map((row) => ({ ...row, monthLabel: fmtMonth(row.month) })),
    [couponsTrend]
  );

  // Per-program coupon series (from couponsByProgram) for when a
  // specific program is selected — the aggregate couponsTrendData
  // above would otherwise show every program's coupons combined,
  // which isn't what "coupon trend for this program" should mean.
  const selectedProgramCouponsData = useMemo(() => {
    if (!selectedProgram) return [];
    const entry = couponsByProgram.find((p) => p.programId === selectedProgram.id);
    return (entry?.series || []).map((row) => ({ ...row, monthLabel: fmtMonth(row.month) }));
  }, [couponsByProgram, selectedProgram]);

  const activeCouponsTrendData = isAllScope ? couponsTrendData : selectedProgramCouponsData;

  const publicRegistrationGrowthData = useMemo(
    () => publicRegistrationGrowth.map((row) => ({ ...row, monthLabel: fmtMonth(row.month) })),
    [publicRegistrationGrowth]
  );
  const hasPublicRegistrationData = publicRegistrationGrowthData.some((row) => Number(row.count || 0) > 0);

  const selectedProgramCourseStatusData = useMemo(() => {
    if (!selectedProgram) return [];
    const byStatus = selectedProgram.coursesByStatus || {};
    return [
      { name: 'פתוחים', value: Number(byStatus.Opened || 0) },
      { name: 'בעריכה', value: Number(byStatus.Editing || 0) },
      { name: 'בארכיון', value: Number(byStatus.Closed || 0) },
    ].filter((d) => d.value > 0);
  }, [selectedProgram]);

  const publicCourseStatusData = useMemo(() => {
    const byStatus = publicCoursesSummary?.byStatus || {};
    return [
      { name: 'פתוחים', value: Number(byStatus.Opened || 0) },
      { name: 'בעריכה', value: Number(byStatus.Editing || 0) },
      { name: 'בארכיון', value: Number(byStatus.Closed || 0) },
    ].filter((d) => d.value > 0);
  }, [publicCoursesSummary]);

  const COURSE_STATUS_COLORS = { 'פתוחים': '#10b981', 'בעריכה': '#9ca3af', 'בארכיון': '#f43f5e' };

  const hasRegistrationData = activeProgramNames.length > 0 && registrationGrowthData.some(
    (row) => activeProgramNames.some((name) => Number(row[name] || 0) > 0)
  );

  // Upcoming lessons, filtered to whatever scope is active.
  const scopedUpcomingLessons = useMemo(() => {
    if (isAllScope) return upcomingLessons;
    if (isPublicScope) return upcomingLessons.filter((l) => l.ownershipType === 'SUPPLIER_INDEPENDENT');
    return upcomingLessons.filter((l) => l.programId === selectedProgram?.id);
  }, [isAllScope, isPublicScope, selectedProgram, upcomingLessons]);

  const isLoading = loading || programsLoading;
  const isTrendsLoading = trendsLoading;
  const [refreshing, setRefreshing] = useState(false);
  const [scopeSwitching, setScopeSwitching] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDashboard(), fetchTrends(), fetchPrograms()]);
    setTimeout(() => setRefreshing(false), 400);
  };

  // Switching scope doesn't need a new fetch — every scope's data was
  // already loaded up front — but swapping straight to different
  // content with zero transition reads as broken/instant rather than
  // "updated". A brief spinner bridges that.
  const handleScopeChange = (nextScope) => {
    setScopeSwitching(true);
    setScope(nextScope);
    setTimeout(() => setScopeSwitching(false), 350);
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8 pb-10 space-y-8">

        <SupplierDashboardHeader refreshing={refreshing} onRefresh={handleRefresh} />

        {!pageReady ? (
          <div className="relative min-h-[220px]">
            <SpinnerOverlay variant="glow" text="טוען לוח בקרה..." dark={false} blur={false} />
          </div>
        ) : (
          <>
            {/* Scope picker */}
            <div className="flex items-center gap-2">
              <label htmlFor="dashboard-scope" className="text-sm font-semibold text-gray-600 whitespace-nowrap">
                הצג נתונים עבור:
              </label>
              <select
                id="dashboard-scope"
                value={scope}
                onChange={(e) => handleScopeChange(e.target.value)}
                className="flex-1 sm:flex-none sm:w-64 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-bold text-gray-800 outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              >
                <option value="all">הכל ביחד</option>
                {relevantPrograms.map((p) => (
                  <option key={p.id} value={String(p.id)}>{p.name}</option>
                ))}
                <option value="public">קורסים ציבוריים</option>
              </select>
            </div>

            {scopeSwitching ? (
              <div className="relative min-h-[300px]">
                <SpinnerOverlay variant="glow" text="מעדכן נתונים..." dark={false} blur={false} />
              </div>
            ) : (
              <>

            {isAllScope && (
            <>

            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-1">
              <KpiCard icon={Layers} label="תוכניות גפ״ן" value={overview?.totalPrograms ?? 0} accent="indigo" loading={isLoading} />
              <KpiCard icon={BookOpenCheck} label="קורסים" value={overview?.totalCourses ?? 0} accent="sky" loading={isLoading} />
              <KpiCard icon={GraduationCap} label="תלמידים" value={overview?.totalStudents ?? 0} accent="purple" loading={isLoading} />
              <KpiCard icon={Wallet} label="הכנסה מאושרת" value={fmtCurrency(overview?.confirmedRevenue)} accent="emerald" loading={isLoading} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard icon={Users} label="מורים בצוות" value={overview?.teamTeachers ?? 0} accent="indigo" loading={isLoading} />
              <KpiCard icon={Ticket} label="קופונים שהונפקו" value={fmtCurrency(overview?.couponsIssued)} accent="amber" loading={isLoading} />
              <KpiCard icon={CheckCircle2} label="קופונים שמומשו" value={fmtCurrency(overview?.couponsRedeemed)} accent="emerald" loading={isLoading} />
              <KpiCard
                icon={TrendingUp}
                label="שיעור מימוש קופונים"
                value={`${overview?.couponsIssued ? Math.round((Number(overview.couponsRedeemed || 0) / Number(overview.couponsIssued)) * 100) : 0}%`}
                accent="sky"
                loading={isLoading}
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Panel
                eyebrow="ביצועים"
                title="הכנסה לפי תוכנית"
                accent="indigo"
                className="lg:col-span-2"
                empty={revenueByProgramData.length === 0}
                emptyText="אין עדיין נתוני הכנסה להצגה."
              >
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart data={revenueByProgramData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={currencyTooltip} contentStyle={ChartTooltipStyle} />
                      <Bar dataKey="revenue" fill={COLORS.indigo} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel
                eyebrow="תוכניות"
                title="תוכניות לפי סטטוס"
                accent="purple"
                empty={programsByStatusData.length === 0}
                emptyText="אין עדיין תוכניות."
              >
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={programsByStatusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                        {programsByStatusData.map((entry, i) => (
                          <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={ChartTooltipStyle} />
                      <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Panel
                eyebrow="קורסים"
                title="קורסים: תוכנית מול ציבורי"
                accent="sky"
                empty={courseSplitData.length === 0}
                emptyText="אין עדיין קורסים."
              >
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={courseSplitData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                        {courseSplitData.map((entry, i) => (
                          <Cell key={entry.name} fill={i === 0 ? COLORS.indigo : COLORS.rose} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={ChartTooltipStyle} />
                      <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel eyebrow="קופונים" title="קופונים: הונפקו מול מומשו" accent="amber">
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={[{ name: 'קופונים', issued: Number(overview?.couponsIssued || 0), redeemed: Number(overview?.couponsRedeemed || 0) }]}
                      layout="vertical"
                      margin={{ top: 4, right: 16, left: 16, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={COLORS.grid} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" hide />
                      <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={ChartTooltipStyle} />
                      <Legend />
                      <Bar dataKey="issued" name="הונפקו" fill={COLORS.amber} radius={[0, 6, 6, 0]} barSize={28} />
                      <Bar dataKey="redeemed" name="מומשו" fill={COLORS.emerald} radius={[0, 6, 6, 0]} barSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            {/* Trend charts — real time series, not just current snapshots */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Panel
                eyebrow="מגמות"
                title="מגמת גידול בהרשמה לפי תוכנית"
                subtitle="מספר תלמידים מצטבר, חודש אחר חודש"
                accent="indigo"
                loading={isTrendsLoading && registrationGrowthData.length === 0}
                empty={!isTrendsLoading && !hasRegistrationData}
                emptyText="אין עדיין נתוני הרשמה להצגת מגמה."
              >
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <LineChart data={registrationGrowthData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                      <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={ChartTooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {activeProgramNames.map((name, i) => (
                        <Line
                          key={name}
                          type="monotone"
                          dataKey={name}
                          stroke={PIE_COLORS[i % PIE_COLORS.length]}
                          strokeWidth={2.5}
                          dot={{ r: 3 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel
                eyebrow="מגמות"
                title="מגמת ניצול קופונים"
                subtitle="הונפקו מול מומשו, חודש אחר חודש"
                accent="amber"
                loading={isTrendsLoading && couponsTrendData.length === 0}
                empty={!isTrendsLoading && couponsTrendData.every((r) => !r.issuedAmount && !r.redeemedAmount)}
                emptyText="אין עדיין נתוני קופונים להצגת מגמה."
              >
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <LineChart data={couponsTrendData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                      <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={ChartTooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="issuedAmount" name="הונפקו" stroke={COLORS.amber} strokeWidth={2.5} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="redeemedAmount" name="מומשו" stroke={COLORS.emerald} strokeWidth={2.5} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Per-program breakdown */}
              <Panel
                eyebrow="תוכניות"
                title="נתונים לפי תוכנית"
                accent="indigo"
                className="lg:col-span-2"
                empty={relevantPrograms.length === 0}
                emptyText="אין עדיין תוכניות עם נתונים להצגה."
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-right text-xs text-gray-500 border-b border-gray-100">
                        <th className="pb-2 font-semibold">תוכנית</th>
                        <th className="pb-2 font-semibold">סטטוס</th>
                        <th className="pb-2 font-semibold">בתי ספר</th>
                        <th className="pb-2 font-semibold">מורים</th>
                        <th className="pb-2 font-semibold">קורסים</th>
                        <th className="pb-2 font-semibold">תלמידים</th>
                        <th className="pb-2 font-semibold">הכנסה</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relevantPrograms.map((p) => (
                        <tr key={p.id} className="border-b border-gray-50 last:border-0">
                          <td className="py-2.5 font-semibold text-gray-900">{p.name}</td>
                          <td className="py-2.5 text-gray-600">{STATUS_LABELS[p.status] || p.status}</td>
                          <td className="py-2.5 text-gray-600">{p.schools ?? 0}</td>
                          <td className="py-2.5 text-gray-600">{p.teachers ?? 0}</td>
                          <td className="py-2.5 text-gray-600">{p.courses ?? 0}</td>
                          <td className="py-2.5 text-gray-600">{p.students ?? 0}</td>
                          <td className="py-2.5 font-semibold text-emerald-700">{fmtCurrency(p.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>

              {/* Public courses summary */}
              <Panel eyebrow="ציבורי" title="קורסים ציבוריים" subtitle="מחוץ לתוכניות" accent="sky">
                <div>
                  <StatRow icon={BookOpenCheck} label="סה״כ קורסים" value={publicCoursesSummary?.total ?? 0} />
                  <StatRow icon={CheckCircle2} label="פתוחים להרשמה" value={publicCoursesSummary?.byStatus?.Opened ?? 0} />
                  <StatRow icon={CalendarClock} label="בעריכה (טרם פורסמו)" value={publicCoursesSummary?.byStatus?.Editing ?? 0} />
                  <StatRow icon={Layers} label="בארכיון" value={publicCoursesSummary?.byStatus?.Closed ?? 0} />
                  <StatRow icon={GraduationCap} label="תלמידים רשומים" value={publicCoursesSummary?.students ?? 0} />
                  <StatRow icon={Wallet} label="הכנסה מאושרת" value={fmtCurrency(publicCoursesSummary?.revenue)} />
                </div>
              </Panel>
            </div>
            </>
            )}

            {selectedProgram && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-1">
                  <KpiCard icon={School} label="בתי ספר" value={selectedProgram.schools ?? 0} accent="indigo" />
                  <KpiCard icon={BookOpenCheck} label="קורסים" value={selectedProgram.courses ?? 0} accent="sky" />
                  <KpiCard icon={GraduationCap} label="תלמידים" value={selectedProgram.students ?? 0} accent="purple" />
                  <KpiCard icon={Wallet} label="הכנסה מאושרת" value={fmtCurrency(selectedProgram.revenue)} accent="emerald" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <KpiCard icon={Users} label="מורים בתוכנית" value={selectedProgram.teachers ?? 0} accent="indigo" />
                  <KpiCard icon={Ticket} label="קופונים שהונפקו" value={fmtCurrency(selectedProgram.couponsIssued)} accent="amber" />
                  <KpiCard icon={CheckCircle2} label="קופונים שמומשו" value={fmtCurrency(selectedProgram.couponsRedeemed)} accent="emerald" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Panel
                    eyebrow="מגמות"
                    title="מגמת גידול בהרשמה"
                    subtitle={selectedProgram.name}
                    accent="indigo"
                    loading={isTrendsLoading && registrationGrowthData.length === 0}
                    empty={!isTrendsLoading && !hasRegistrationData}
                    emptyText="אין עדיין נתוני הרשמה להצגת מגמה."
                  >
                    <div style={{ width: '100%', height: 240 }}>
                      <ResponsiveContainer>
                        <LineChart data={registrationGrowthData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                          <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip contentStyle={ChartTooltipStyle} />
                          <Line type="monotone" dataKey={selectedProgram.name} stroke={COLORS.indigo} strokeWidth={2.5} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>

                  <Panel
                    eyebrow="מגמות"
                    title="מגמת ניצול קופונים"
                    subtitle={selectedProgram.name}
                    accent="amber"
                    loading={isTrendsLoading && activeCouponsTrendData.length === 0}
                    empty={!isTrendsLoading && activeCouponsTrendData.every((r) => !r.issuedAmount && !r.redeemedAmount)}
                    emptyText="אין עדיין נתוני קופונים להצגת מגמה."
                  >
                    <div style={{ width: '100%', height: 240 }}>
                      <ResponsiveContainer>
                        <LineChart data={activeCouponsTrendData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                          <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={ChartTooltipStyle} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Line type="monotone" dataKey="issuedAmount" name="הונפקו" stroke={COLORS.amber} strokeWidth={2.5} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="redeemedAmount" name="מומשו" stroke={COLORS.emerald} strokeWidth={2.5} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>

                  <Panel
                    eyebrow="קורסים"
                    title="קורסים לפי סטטוס"
                    subtitle={selectedProgram.name}
                    accent="purple"
                    empty={selectedProgramCourseStatusData.length === 0}
                    emptyText="אין עדיין קורסים בתוכנית זו."
                  >
                    <div style={{ width: '100%', height: 240 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={selectedProgramCourseStatusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                            {selectedProgramCourseStatusData.map((entry) => (
                              <Cell key={entry.name} fill={COURSE_STATUS_COLORS[entry.name]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={ChartTooltipStyle} />
                          <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>
                </div>
              </>
            )}

            {isPublicScope && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-1">
                  <KpiCard icon={BookOpenCheck} label="סה״כ קורסים" value={publicCoursesSummary?.total ?? 0} accent="sky" />
                  <KpiCard icon={CheckCircle2} label="פתוחים להרשמה" value={publicCoursesSummary?.byStatus?.Opened ?? 0} accent="emerald" />
                  <KpiCard icon={GraduationCap} label="תלמידים רשומים" value={publicCoursesSummary?.students ?? 0} accent="purple" />
                  <KpiCard icon={Wallet} label="הכנסה מאושרת" value={fmtCurrency(publicCoursesSummary?.revenue)} accent="emerald" />
                </div>
                <p className="text-xs text-gray-400">
                  קופונים חלים רק על קורסים בתוכניות גפ״ן — לקורסים ציבוריים אין קופונים.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Panel
                    eyebrow="מגמות"
                    title="מגמת גידול בהרשמה"
                    subtitle="קורסים ציבוריים"
                    accent="sky"
                    loading={isTrendsLoading && publicRegistrationGrowthData.length === 0}
                    empty={!isTrendsLoading && !hasPublicRegistrationData}
                    emptyText="אין עדיין נתוני הרשמה להצגת מגמה."
                  >
                    <div style={{ width: '100%', height: 240 }}>
                      <ResponsiveContainer>
                        <LineChart data={publicRegistrationGrowthData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                          <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip contentStyle={ChartTooltipStyle} />
                          <Line type="monotone" dataKey="count" name="תלמידים" stroke={COLORS.rose} strokeWidth={2.5} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>

                  <Panel
                    eyebrow="קורסים"
                    title="קורסים לפי סטטוס"
                    subtitle="קורסים ציבוריים"
                    accent="purple"
                    empty={publicCourseStatusData.length === 0}
                    emptyText="אין עדיין קורסים ציבוריים."
                  >
                    <div style={{ width: '100%', height: 240 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={publicCourseStatusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                            {publicCourseStatusData.map((entry) => (
                              <Cell key={entry.name} fill={COURSE_STATUS_COLORS[entry.name]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={ChartTooltipStyle} />
                          <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>
                </div>
              </>
            )}

            {/* Upcoming lessons — shared across every scope */}
            <Panel
              eyebrow="לוח זמנים"
              title="שיעורים קרובים"
              accent="slate"
              empty={scopedUpcomingLessons.length === 0}
              emptyText="אין שיעורים קרובים מתוזמנים."
            >
              <div className="space-y-2">
                {scopedUpcomingLessons.map((l) => (
                  <div
                    key={l.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 text-sm truncate">{l.courseTitle}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {l.title}
                        {l.programName ? ` · ${l.programName}` : ' · קורס ציבורי'}
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-left">
                      <div className="text-sm font-semibold text-gray-800">{fmtDate(l.date)}</div>
                      <div className="text-xs text-gray-500">{l.startTime}–{l.endTime}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupplierDashboard;